import { useEffect } from "react";
import useRefreshToken from "./useRefreshToken";
import useAuth from "./useAuth";
import { apiClientPrivate } from "../adapters/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const useAxiosPrivate = () => {
  const refresh = useRefreshToken();
  const { auth, setAuth, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const requestIntercept = apiClientPrivate.interceptors.request.use(
      (config) => {
        if (!config.headers["Authorization"]) {
          config.headers["Authorization"] = `Bearer ${auth?.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseIntercept = apiClientPrivate.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;
        if (error?.response?.status === 401 && !prevRequest?.sent) {
          prevRequest.sent = true;
          try {
            const newAccessToken = await refresh();
            if (!newAccessToken) {
              // If new access token is not obtained, trigger full logout
              await logout();
              toast.error("Session expired. Please log in again.");
              navigate("/", { replace: true });
              return Promise.reject(error); // Stop retrying the request
            }
            prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            return apiClientPrivate(prevRequest); // Retry the request with new access token
          } catch (refreshError) {
            // console.error("Error refreshing access token:", refreshError);
            await logout();
            toast.error("Session expired. Please log in again.");
            navigate("/", { replace: true });
            return Promise.reject(error);
          }
        } else if (error?.response?.status === 401) {
          // If it's a 401 but we've already tried refreshing, log them out
          await logout();
          toast.error("Session expired. Please log in again.");
          navigate("/", { replace: true });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      apiClientPrivate.interceptors.request.eject(requestIntercept);
      apiClientPrivate.interceptors.response.eject(responseIntercept);
    };
  }, [auth, refresh, setAuth]);

  return apiClientPrivate;
};

export default useAxiosPrivate;
